import { Component } from '@theme/component';

/**
 * Sale countdown.
 *
 * Every instance counts down to one absolute deadline supplied by Liquid as a
 * Unix timestamp, so the remaining time is derived from the real clock rather
 * than decremented locally. A page load, a section re-render or a throttled
 * background tab therefore never restarts or skews the countdown.
 *
 * A single shared interval drives every instance on the page. Instances
 * register on connect and deregister on disconnect, so the theme editor's
 * repeated section renders cannot accumulate duplicate timers.
 */

const TICK_MS = 1000;
const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/** @type {Set<SaleCountdown>} */
const instances = new Set();

/** @type {ReturnType<typeof setInterval> | null} */
let timerId = null;

function renderAll() {
  const now = Date.now();
  for (const instance of instances) instance.render(now);
}

function startTicking() {
  if (timerId === null && instances.size > 0) timerId = setInterval(renderAll, TICK_MS);
}

function stopTicking() {
  if (timerId !== null && instances.size === 0) {
    clearInterval(timerId);
    timerId = null;
  }
}

/**
 * @param {number} value
 * @returns {string} The value padded to at least two digits.
 */
function pad(value) {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * Formats whole seconds as DD:HH:MM:SS, dropping the day segment under 24 hours.
 *
 * @param {number} totalSeconds
 * @returns {string}
 */
function formatRemaining(totalSeconds) {
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const time = `${pad(hours)}H:${pad(minutes)}M:${pad(seconds)}S`;

  return days > 0 ? `${pad(days)}D:${time}` : time;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} display - The element holding the ticking value.
 */

/** @extends {Component<Refs>} */
class SaleCountdown extends Component {
  requiredRefs = ['display'];

  /** Deadline in milliseconds since the epoch. */
  #deadline = 0;

  /** Set once the deadline has passed, so the ended state is written only once. */
  #ended = false;

  connectedCallback() {
    super.connectedCallback();

    const deadlineSeconds = Number(this.dataset.deadline);
    if (!Number.isFinite(deadlineSeconds) || deadlineSeconds <= 0) return;

    this.#deadline = deadlineSeconds * TICK_MS;
    instances.add(this);
    this.render(Date.now());
    startTicking();
  }

  disconnectedCallback() {
    instances.delete(this);
    stopTicking();
    super.disconnectedCallback();
  }

  /**
   * Writes the remaining time, or the ended state once the deadline passes.
   *
   * @param {number} now - Current time in milliseconds since the epoch.
   */
  render(now) {
    const { display } = this.refs;
    if (!display) return;

    const remaining = Math.max(0, Math.floor((this.#deadline - now) / TICK_MS));

    if (remaining <= 0) {
      if (this.#ended) return;
      this.#ended = true;
      display.textContent = this.dataset.endedText ?? '';
      this.setAttribute('data-ended', 'true');
      // Stop counting rather than resetting to a new deadline.
      instances.delete(this);
      stopTicking();
      return;
    }

    const next = formatRemaining(remaining);
    if (display.textContent !== next) display.textContent = next;
  }
}

if (!customElements.get('sale-countdown')) {
  customElements.define('sale-countdown', SaleCountdown);
}
